import React from 'react';
import DatePicker from 'react-datepicker';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { FormHelperText } from '@mui/material';
import UIKitIcon from './Icon';

export type UIKitDatepickerSize = 'medium' | 'small';

export interface UIKitDatepickerProps {
  name: string;
  selectedDate: Date | null;
  onChange: (newValue: Date | null) => void;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  disabled?: boolean;
  readonly?: boolean;
  label?: string;
  htmlId?: string;
  size?: UIKitDatepickerSize;
  error?: boolean;
  helperText?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  labelClassName?: string;
  labelStyle?: React.CSSProperties;
}

const Root = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const Label = styled('label', {
  shouldForwardProp: (prop) => prop !== 'uiSize' && prop !== 'isError',
})<{ uiSize: UIKitDatepickerSize; isError?: boolean }>(({ theme, uiSize, isError }) => ({
  fontWeight: 600,
  fontSize: 14,
  lineHeight: '20px',
  marginBottom: uiSize === 'small' ? 4 : 8,
  color: isError ? theme.palette.error.main : theme.palette.text.primary,
  fontStyle: 'normal',
}));

const InputGroup = styled('div')({
  marginTop: 0,
});

const DatePickerWrapper = styled('div')({
  position: 'relative',
});

const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: (prop) => prop !== 'uiSize' && prop !== 'isError',
})<{ uiSize: UIKitDatepickerSize; isError?: boolean; disabled?: boolean; readOnly?: boolean }>(
  ({ theme, uiSize, isError, disabled, readOnly }) => ({
    borderRadius: 4,
    display: 'block',
    width: '100%',
    height: uiSize === 'small' ? 40 : 56,
    fontSize: uiSize === 'small' ? 14 : 16,
    fontWeight: 400,
    padding: uiSize === 'small' ? '10px 12px' : '18px 12px',
    border: `1px solid ${isError ? theme.palette.error.main : '#A6A6A8'} !important`,
    backgroundColor: theme.palette.background.paper,
    cursor: disabled || readOnly ? 'default !important' : 'inherit',

    '&:hover': {
      borderColor: '#535354 !important',
    },

    '&:focus': {
      borderColor: isError ? theme.palette.error.main : theme.palette.primary.main,
      outline: 'none',
      boxShadow: `0 0 0 2px ${isError ? theme.palette.error.main : theme.palette.primary.main}22`,
    },

    '&:disabled': {
      backgroundColor: '#f5f5f5',
      color: '#999',
      cursor: 'not-allowed',
      opacity: 1,
    },

    '&::placeholder': {
      color: '#999',
      opacity: 1,
    },
  })
);

const StyledIcon = styled(UIKitIcon)({
  position: 'absolute',
  right: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  color: 'inherit',
});

const StyledHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== 'isError',
})<{ isError?: boolean }>(({ theme, isError }) => ({
  fontSize: 12,
  lineHeight: 1.4,
  color: `${isError ? theme.palette.error.main : theme.palette.text.secondary} !important`,
}));

const UIKitDatepicker: React.FC<UIKitDatepickerProps> = ({
  name,
  selectedDate,
  onChange,
  onBlur,
  disabled = false,
  readonly = false,
  label,
  htmlId,
  size = 'medium',
  error = false,
  className,
  style,
  labelClassName,
  labelStyle,
  helperText,
}: UIKitDatepickerProps) => (
  <Root className={className} style={style}>
    {label && (
      <Label
        id={`${htmlId || name}-label`}
        uiSize={size}
        isError={!!error}
        className={labelClassName}
        style={labelStyle}
      >
        {label}
      </Label>
    )}

    <InputGroup className="input-group input-group-date-picker-wrapper">
      <DatePickerWrapper className="form__form-group-field form__date-picker-wrapper">
        <StyledDatePicker
          id={htmlId || name}
          name={name}
          selected={selectedDate}
          onChange={onChange}
          onBlur={onBlur}
          dateFormat="dd/MM/yyyy"
          uiSize={size}
          isError={!!error}
          disabled={disabled}
          readOnly={readonly || disabled}
          className={clsx('form-control-date-picker grievance-date')}
          autoComplete="off"
          placeholderText="DD/MM/YYYY"
          aria-labelledby={`${htmlId || name}-label`}
        />
        <StyledIcon size="m" name="today" className="grievance-date-icon" />
      </DatePickerWrapper>
      {helperText && <StyledHelperText isError={!!error}>{helperText}</StyledHelperText>}
    </InputGroup>
  </Root>
);

export default UIKitDatepicker;
